import { useState, useEffect, Fragment } from "react";
import { Typography, Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { findProductById, updateProduct } from "../../../Redux/Customers/Product/Action";
import { useParams } from "react-router-dom";

const initialSizes = [
  { name: "One Unit", quantity: 0 },
  { name: "Small Box", quantity: 0 },
  { name: "Large Box", quantity: 0 },
  { name: "XL Box", quantity: 0 },
  { name: "Bulk Box", quantity: 0 }
];

const topLevelCategories = [
  { value: 'seasonal_fruits', label: 'Seasonal Fruits' },
  { value: 'tropical_fruits', label: 'Tropical Fruits' },
];

const secondLevelCategories = {
  seasonal_fruits: [
    { value: 'seasonal_produce', label: 'Seasonal Produce' },
  ],
  tropical_fruits: [
    { value: 'fresh_fruits', label: 'Fresh Fruits' },
  ],
};

const thirdLevelCategories = {
  seasonal_produce: [
    { value: 'summer_fruits', label: 'Summer Fruits' },
    { value: 'winter_fruits', label: 'Winter Fruits' },
    { value: 'monsoon_fruits', label: 'Monsoon Fruits' },
    { value: 'all_season', label: 'All Season' },
  ],
  fresh_fruits: [
    { value: 'mangoes', label: 'Mangoes' },
    { value: 'guava', label: 'Guava' },
    { value: 'sapota', label: 'Sapota' },
    { value: 'seethaphal', label: 'Seethaphal' },
    { value: 'custard_apple', label: 'Custard Apple' },
    { value: 'grapes', label: 'Grapes' },
    { value: 'watermelon', label: 'Watermelon' },
    { value: 'papaya', label: 'Papaya' },
    { value: 'mixed_fruit_baskets', label: 'Mixed Fruit Baskets' },
  ],
};

const UpdateProductForm = () => {
  const [productData, setProductData] = useState({
    imageUrl: "",
    brand: "Siri Farms",
    title: "",
    color: "",
    discountedPrice: "",
    price: "",
    discountPersent: "",
    size: initialSizes,
    quantity: "",
    topLavelCategory: "",
    secondLavelCategory: "",
    thirdLavelCategory: "",
    description: "",
  });
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { productId } = useParams();
  const { customersProduct } = useSelector((store) => store);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    dispatch(findProductById({ productId }));
  }, [productId]);

  useEffect(() => {
    if (customersProduct.product) {
      const product = customersProduct.product;
      setProductData({
        imageUrl: product.imageUrl || "",
        brand: product.brand || "Siri Farms",
        title: product.title || "",
        color: product.color || "",
        discountedPrice: product.discountedPrice || "",
        price: product.price || "",
        discountPersent: product.discountPersent || "",
        size: product.sizes && product.sizes.length > 0 ? product.sizes : initialSizes,
        quantity: product.quantity || "",
        topLavelCategory: product.category?.parentCategory?.parentCategory?.name || "",
        secondLavelCategory: product.category?.parentCategory?.name || "",
        thirdLavelCategory: product.category?.name || "",
        description: product.description || "",
      });
    }
  }, [customersProduct.product]);

  // Auto-calculate total quantity from box sizes
  useEffect(() => {
    const totalQuantity = productData.size.reduce((acc, curr) => acc + (parseInt(curr.quantity) || 0), 0);
    setProductData((prev) => ({
      ...prev,
      quantity: totalQuantity,
    }));
  }, [productData.size]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (name === 'topLavelCategory') {
      setProductData(prev => ({ ...prev, secondLavelCategory: '', thirdLavelCategory: '' }));
    } else if (name === 'secondLavelCategory') {
      setProductData(prev => ({ ...prev, thirdLavelCategory: '' }));
    }
  };

  const handleSizeChange = (e, index) => {
    let { name, value } = e.target;
    name === "size_quantity" ? (name = "quantity") : (name = e.target.name);

    const sizes = [...productData.size];
    sizes[index][name] = value;
    setProductData((prevState) => ({
      ...prevState,
      size: sizes,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const activeSizes = productData.size.filter(s => parseInt(s.quantity) > 0);
    const totalQuantity = productData.size.reduce((acc, curr) => acc + (parseInt(curr.quantity) || 0), 0);

    const finalData = {
      ...productData,
      productId,
      sizes: activeSizes,
      quantity: productData.quantity || totalQuantity
    };

    try {
      await dispatch(updateProduct(finalData));
      setSnackbarMessage("Product updated successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (err) {
      setSnackbarMessage("Failed to update product");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const getSecondLevelOptions = () => {
    const topLevel = productData.topLavelCategory;
    return (topLevel && secondLevelCategories[topLevel]) ? secondLevelCategories[topLevel] : [];
  };

  const getThirdLevelOptions = () => {
    const secondLevel = productData.secondLavelCategory;
    return (secondLevel && thirdLevelCategories[secondLevel]) ? thirdLevelCategories[secondLevel] : [];
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  return (
    <Fragment>
      <Typography variant="h3" sx={{ textAlign: "center", mb: 4, fontWeight: 'bold', color: '#4A7C3F' }} className="py-10">
        Update Product
      </Typography>
      <form onSubmit={handleSubmit} className="createProductContainer min-h-screen">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Image URL" name="imageUrl" value={productData.imageUrl} onChange={handleChange} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Brand" name="brand" value={productData.brand} onChange={handleChange} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Title" name="title" value={productData.title} onChange={handleChange} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Variety/Color" name="color" value={productData.color} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Stock Quantity (auto-calculated)" name="quantity" value={productData.quantity} type="number" required InputProps={{ readOnly: true }} helperText="Auto-calculated from box quantities below" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Price" name="price" value={productData.price} onChange={handleChange} type="number" required />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Discounted Price" name="discountedPrice" value={productData.discountedPrice} onChange={handleChange} type="number" required />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Discount Percentage" name="discountPersent" value={productData.discountPersent} onChange={handleChange} type="number" required />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>Top Level Category</InputLabel>
              <Select name="topLavelCategory" value={productData.topLavelCategory} onChange={handleChange} label="Top Level Category">
                {topLevelCategories.map((cat) => <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>Second Level Category</InputLabel>
              <Select name="secondLavelCategory" value={productData.secondLavelCategory} onChange={handleChange} label="Second Level Category" disabled={!productData.topLavelCategory}>
                {getSecondLevelOptions().map((cat) => <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>Third Level Category</InputLabel>
              <Select name="thirdLavelCategory" value={productData.thirdLavelCategory} onChange={handleChange} label="Third Level Category" disabled={!productData.secondLavelCategory}>
                {getThirdLevelOptions().map((cat) => <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth label="Description" multiline name="description" rows={4} onChange={handleChange} value={productData.description} required />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Box Inventory Tracking</Typography>
          </Grid>
          {productData.size.map((size, index) => (
            <Grid container item spacing={3} key={index}>
              <Grid item xs={12} sm={6}>
                <TextField label="Box Size" name="name" value={size.name} required fullWidth disabled />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Available Boxes" name="size_quantity" type="number" value={size.quantity} onChange={(event) => handleSizeChange(event, index)} required fullWidth />
              </Grid>
            </Grid>
          ))}
          <Grid item xs={12} sx={{ mt: 4 }}>
            <Button variant="contained" sx={{ p: 1.5, minWidth: '200px', fontWeight: 'bold' }} color="primary" size="large" type="submit" disabled={customersProduct.loading}>
              {customersProduct.loading ? "Updating..." : "Update Product"}
            </Button>
          </Grid>
        </Grid>

        <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </form>
    </Fragment>
  );
};

export default UpdateProductForm;

